import { useCallback, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash-es';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { useStore } from '@/pages/aigc/hooks/useContext';
import { IAIContentRef } from '../AIContent/_type';
import { extractMetricDataKey } from '../AIContent/_utils';
import { useLocalState } from '../../hooks/useLocalState';

interface IChartConfigItem {
  newFilterInfoVal: { [key: string]: string; } | null;
  currentMetric: string;
}

interface IUseDSChartConfigParams {
  chartDataId: string;
  aiContentRef: React.RefObject<IAIContentRef>;
  hideSource: boolean;
}

interface IUseDSChartConfigReturn {
  // 回调函数
  onFilterChange: (
    newFilterInfoVal: { [key: string]: string; } | null,
    currentMetric: string,
  ) => void;
  onMetricChange: (
    newMetric: string,
    filterInfoVal: { [key: string]: string; } | null,
  ) => void;
  defaultShowFilters: boolean;
  defaultShowMetrics: boolean;
}

/**
 * DS 报告专用 Hook
 * 设置指标、筛选器的高亮
 */
export const useDSChartConfig = (
  params: IUseDSChartConfigParams,
): IUseDSChartConfigReturn => {
  const { chartDataId, aiContentRef, hideSource } = params;
  const { template } = useSearchParamsAppoint();
  const [isDSReportSession] = useLocalState('__dba_is_ds_report_session__', false);
  const isDSReport = template === 'dsReport' || isDSReportSession;

  // DS 报告的全局配置状态
  const [chartsConfig, setChartsConfig] = useStore<{
    [key in string]: IChartConfigItem;
  }>('chartsAiSetting', {});
  // 从全局配置中获取保存的值
  const savedConfig = useMemo(
    () => chartsConfig?.[chartDataId],
    [chartsConfig, chartDataId],
  );
  // 设置初始值
  useEffect(() => {
    if (!isDSReport || !aiContentRef?.current) {
      return;
    }
    const showMetrics = aiContentRef?.current?.getShowMetrics?.();
    let newMetric = savedConfig?.currentMetric;

    if (!isEmpty(savedConfig?.newFilterInfoVal)) {
      aiContentRef?.current?.setFilterInfoVal(savedConfig?.newFilterInfoVal);
    }
    // 检查当前指标是否在可用的指标列表中
    const isCurrentMetricValid = newMetric && showMetrics?.some((metric) => {
      const metricKey =
        extractMetricDataKey(metric.data_key) || metric.metric_key || '';
      return metricKey === newMetric || metric?.metric_key === newMetric;
    });
    // 如果当前指标无效且存在可用指标，则选择第一个可用指标
    if (!isCurrentMetricValid && showMetrics?.length > 0) {
      newMetric = extractMetricDataKey(showMetrics[0]?.data_key) || showMetrics[0]?.metric_key || '' as string;
    }
    aiContentRef?.current?.setCurrentMetric(newMetric);
  }, [isDSReport, chartDataId, aiContentRef]);

  // 处理筛选值变化
  const handleFilterChange = useCallback(
    (
      newFilterInfoVal: { [key: string]: string; } | null,
      currentMetric: string,
    ) => {
      if (!isDSReport || !hideSource) return;

      const chartConfigItem: IChartConfigItem = {
        newFilterInfoVal,
        currentMetric,
      };

      setChartsConfig({
        ...chartsConfig,
        [chartDataId]: chartConfigItem,
      });
    },
    [isDSReport, hideSource, setChartsConfig, chartsConfig, chartDataId],
  );

  // 处理指标变化
  const handleMetricChange = useCallback(
    (newMetric: string, filterInfoVal: { [key: string]: string; } | null) => {
      if (!isDSReport || !hideSource) return;

      const chartConfigItem: IChartConfigItem = {
        newFilterInfoVal: filterInfoVal,
        currentMetric: newMetric,
      };

      setChartsConfig({
        ...chartsConfig,
        [chartDataId]: chartConfigItem,
      });
    },
    [isDSReport, hideSource, setChartsConfig, chartsConfig, chartDataId],
  );
  return {
    onFilterChange: handleFilterChange,
    onMetricChange: handleMetricChange,
    defaultShowFilters: !isDSReport,
    defaultShowMetrics: !isDSReport,
  };
};
